import React, { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  Collapse,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material'
import { Refresh, ExpandMore, ExpandLess } from '@mui/icons-material'
import { getProductSnapshots, getProductSnapshot } from '../../../../lib/requests.es6.js'

const navy = '#173E4B'

const TH = {
  fontSize: 11,
  fontWeight: 700,
  color: '#6B7280',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  bgcolor: '#F9FAFB',
  borderBottom: '1px solid #E5E7EB'
}

function fmtDate(d) {
  if (!d) return 'N/A'
  const dt = new Date(d)
  if (isNaN(dt.getTime())) return String(d)
  return dt.toLocaleString()
}

// Read-only version history. Snapshots are captured server-side whenever an
// approved change bumps the product version (pruned to PRODUCT_SNAPSHOT_MAX).
function SnapshotsPanel(props) {
  const { productGuid } = props
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [list, setList] = useState([])
  const [openVersion, setOpenVersion] = useState(null)
  const [details, setDetails] = useState({}) // version -> { loading, data, error }

  const load = useCallback(() => {
    if (!productGuid) return
    setLoading(true)
    setError(null)
    getProductSnapshots(productGuid)
      .then((res) => setList(Array.isArray(res.data) ? res.data : []))
      .catch((e) =>
        setError((e && e.response && e.response.data && e.response.data.message) || e.message || 'Failed to load snapshots')
      )
      .finally(() => setLoading(false))
  }, [productGuid])

  useEffect(() => {
    load()
  }, [load])

  const toggle = (version) => {
    if (openVersion === version) {
      setOpenVersion(null)
      return
    }
    setOpenVersion(version)
    if (!details[version]) {
      setDetails((d) => ({ ...d, [version]: { loading: true } }))
      getProductSnapshot(productGuid, version)
        .then((res) => setDetails((d) => ({ ...d, [version]: { loading: false, data: res.data } })))
        .catch((e) =>
          setDetails((d) => ({
            ...d,
            [version]: {
              loading: false,
              error: (e && e.response && e.response.data && e.response.data.message) || e.message || 'Failed to load snapshot'
            }
          }))
        )
    }
  }

  const renderDetail = (version) => {
    const entry = details[version]
    if (!entry) return null
    if (entry.loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={22} sx={{ color: navy }} />
        </Box>
      )
    }
    if (entry.error) {
      return <Alert severity="error" sx={{ my: 1 }}>{entry.error}</Alert>
    }
    const snap = (entry.data && entry.data.snapshot) || entry.data || {}
    const packages = snap.packages || []
    const addOns = snap.add_ons || []
    return (
      <Box sx={{ py: 1.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          <Chip size="small" label={'Name: ' + (snap.name || 'N/A')} sx={{ bgcolor: '#eef2f4', color: navy }} />
          <Chip size="small" label={'Type: ' + (snap.type || 'N/A')} sx={{ bgcolor: '#eef2f4', color: navy }} />
          <Chip size="small" label={packages.length + ' package' + (packages.length !== 1 ? 's' : '')} sx={{ bgcolor: '#eef2f4', color: navy }} />
          <Chip size="small" label={addOns.length + ' add-on' + (addOns.length !== 1 ? 's' : '')} sx={{ bgcolor: '#eef2f4', color: navy }} />
          {snap.currency && <Chip size="small" label={snap.currency} sx={{ bgcolor: '#eef2f4', color: navy }} />}
        </Box>
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Raw snapshot
        </Typography>
        <Box
          component="pre"
          sx={{
            mt: 0.5,
            m: 0,
            p: 1.5,
            bgcolor: '#0f172a',
            color: '#e2e8f0',
            borderRadius: 1,
            fontSize: 11.5,
            lineHeight: 1.5,
            maxHeight: 320,
            overflow: 'auto'
          }}
        >
          {JSON.stringify(snap, null, 2)}
        </Box>
      </Box>
    )
  }

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1f2937' }}>
              Version History
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Version-stamped snapshots captured when an approved change bumps the product version
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={load} disabled={loading} sx={{ color: navy }}>
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: navy }} />
          </Box>
        ) : list.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No snapshots yet — they are captured when an approved change request bumps the product version.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={TH}>Version</TableCell>
                  <TableCell sx={TH}>Captured</TableCell>
                  <TableCell sx={TH} align="right">Snapshot</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((s) => (
                  <React.Fragment key={s.guid || s.version}>
                    <TableRow hover>
                      <TableCell>
                        <Chip size="small" label={'v' + s.version} sx={{ fontWeight: 600, bgcolor: '#eef2f4', color: navy }} />
                      </TableCell>
                      <TableCell sx={{ color: '#374151' }}>{fmtDate(s.captured_at)}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          onClick={() => toggle(s.version)}
                          endIcon={openVersion === s.version ? <ExpandLess /> : <ExpandMore />}
                          sx={{ textTransform: 'none', color: navy }}
                        >
                          {openVersion === s.version ? 'Hide' : 'View'}
                        </Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3} sx={{ py: 0, borderBottom: openVersion === s.version ? undefined : 'none' }}>
                        <Collapse in={openVersion === s.version} timeout="auto" unmountOnExit>
                          {renderDetail(s.version)}
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  )
}

export default SnapshotsPanel
